import {
    Component,
    Input,
    Output,
    EventEmitter,
    ChangeDetectionStrategy,
    ViewEncapsulation,
    HostBinding,
    HostListener
} from "@angular/core";

@Component({
    selector: "app-toggle-switch",
    standalone: true,
    encapsulation: ViewEncapsulation.ShadowDom, // CSS truly isolated
    changeDetection: ChangeDetectionStrategy.OnPush,
    template: `
        <div class="track" [attr.data-state]="_checked ? 'on' : 'off'">
            <span class="label on">{{ onLabel }}</span>
            <span class="label off">{{ offLabel }}</span>
            <div class="thumb"></div>
        </div>
    `,
    styles: [
        `
            :host {
                --ts-width: 52px;
                --ts-height: 28px;
                --ts-bg-off: #c7c9d1;
                --ts-bg-on: #22c55e;
                --ts-thumb: #ffffff;
                --ts-ring: rgba(59, 130, 246, 0.6);

                display: inline-block;
                outline: none;
                cursor: pointer;
                user-select: none;
                font-family:
                    ui-sans-serif,
                    system-ui,
                    -apple-system,
                    "Segoe UI",
                    Roboto,
                    "Helvetica Neue",
                    Arial;
            }
            :host(.readonly) {
                cursor: default;
                opacity: 0.75;
            }

            .track {
                width: var(--ts-width);
                height: var(--ts-height);
                border-radius: calc(var(--ts-height) / 2);
                background: var(--ts-bg-off);
                position: relative;
                transition: background-color 0.15s ease-in-out;
                box-sizing: border-box;
            }
            :host(.checked) .track {
                background: var(--ts-bg-on);
            }

            .thumb {
                position: absolute;
                top: 3px;
                left: 3px;
                width: calc(var(--ts-height) - 6px);
                height: calc(var(--ts-height) - 6px);
                border-radius: 50%;
                background: var(--ts-thumb);
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
                transition: transform 0.15s ease-in-out;
            }
            :host(.checked) .thumb {
                transform: translateX(calc(var(--ts-width) - var(--ts-height)));
            }

            .label {
                position: absolute;
                top: 50%;
                transform: translateY(-50%);
                font-size: 12px;
                line-height: 1;
                color: #111827;
                pointer-events: none;
                opacity: 0.9;
            }
            .label.on {
                left: 6px;
            }
            .label.off {
                right: 8px;
            }
            :host(.checked) .label.off {
                opacity: 0;
            }
            :host(:not(.checked)) .label.on {
                opacity: 0;
            }

            :host(:focus-visible) .track {
                box-shadow: 0 0 0 3px var(--ts-ring);
            }
        `
    ]
})
export class ToggleSwitchComponent {
    _checked = false;
    _readonly = false;
    _autoToggle = true;

    @Input() onLabel = "On";
    @Input() offLabel = "Off";

    @Input()
    get autoToggle() {
        return this._autoToggle;
    }
    set autoToggle(v: boolean) {
        this._autoToggle = this.coerceBool(v);
    }

    @Input()
    get checked() {
        return this._checked;
    }
    set checked(v: boolean) {
        this._checked = this.coerceBool(v);
    }

    @Input()
    get readonly() {
        return this._readonly;
    }
    set readonly(v: boolean) {
        this._readonly = this.coerceBool(v);
    }

    @Output() checkedChange = new EventEmitter<boolean>();

    @HostBinding("attr.role") role = "switch";
    @HostBinding("attr.aria-checked") get ariaChecked() {
        return String(this._checked);
    }
    @HostBinding("attr.aria-readonly") get ariaReadonly() {
        return String(this._readonly);
    }
    @HostBinding("attr.tabindex") get tabIndex() {
        return this._readonly ? -1 : 0;
    }

    // Host classes for styling states
    @HostBinding("class.checked") get checkedClass() {
        return this._checked;
    }
    @HostBinding("class.readonly") get readonlyClass() {
        return this._readonly;
    }

    @HostListener("click") onClick() {
        this.toggle();
    }
    @HostListener("keydown", ["$event"])
    onKeydown(e: KeyboardEvent) {
        if (this._readonly || !this.autoToggle) return;
        if (e.key === " " || e.key === "Spacebar" || e.key === "Enter") {
            e.preventDefault();
            this.toggle();
        }
    }

    toggle() {
        if (this._readonly || !this.autoToggle) return;
        this._checked = !this._checked;
        this.checkedChange.emit(this._checked);
    }

    private coerceBool(v: any): boolean {
        return v != null && `${v}` !== "false";
    }
}
